import type { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import type { Scene } from "@babylonjs/core/scene";
import { ctx2d, makeDynamicTexture, type Ctx2D } from "../core/materials";
import type { TagFlag } from "../core/flagImages";
import { CIVIC_COLORS, TOKENS } from "../core/palette";
import { OFFICE_LISTINGS_COPY, layoutListingsBoard, type BoardText, type ListingLineText, type ListingsCopy } from "./boardLayout";
import { BILLBOARD_TEXTURE, containRect, layoutPlaceholder, layoutTextCreative, type PlaceholderCopy } from "./billboardLayout";
import { SIGN_TEXTURE, fitFontSize, fitTextBlock, placeLines, type Region, type SignAtlas } from "./layout";
import { AI_BADGE, layoutAiNameTag } from "./nameTagLayout";
import {
  drawHouse,
  font,
  hexWithAlpha,
  makeMeasure,
  makeScaledMeasure,
  paintBusinessCanvas,
  paintMessageBoardCanvas,
  readableTextOn,
  roundedRect,
  type BusinessSignSpec,
  type SignImage,
} from "./signCanvas";

/**
 * Everything drawn onto DynamicTextures: business sign atlases, the civic sign, the For Sale lot
 * sign, interior boards, the default Townsy creative, name tags and the exit mat. Pure layout
 * decisions live in ./layout.ts and ./boardLayout.ts; this file only paints (canvas text only:
 * owner-written text is never interpreted as markup). The canvas drawing of business signs and
 * message boards lives in ./signCanvas.ts (no Babylon), shared with the store screen's preview.
 */
export { colorDistance, font, makeMeasure, makeScaledMeasure, readableTextOn, type BusinessSignSpec, type SignImage } from "./signCanvas";

/**
 * Paint every atlas slot (rooftop board, banners) for one business. `scale` < 1 paints the same atlas into a smaller
 * texture (the far sign, ADR-0056): the layout stays in SIGN_TEXTURE units and the canvas transform scales it, so the
 * meshes' UVs fit both.
 */
export function paintBusinessTexture(tex: DynamicTexture, spec: BusinessSignSpec, atlas: SignAtlas, scale = 1): void {
  const ctx = ctx2d(tex);
  ctx.save();
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
  paintBusinessCanvas(ctx, spec, atlas);
  ctx.restore();
  tex.update();
}

export function createBusinessTexture(scene: Scene, name: string, spec: BusinessSignSpec, atlas: SignAtlas, scale = 1): DynamicTexture {
  const tex = makeDynamicTexture(scene, name, Math.round(SIGN_TEXTURE.width * scale), Math.round(SIGN_TEXTURE.height * scale), { mips: true });
  tex.wrapU = Texture.CLAMP_ADDRESSMODE;
  tex.wrapV = Texture.CLAMP_ADDRESSMODE;
  paintBusinessTexture(tex, spec, atlas, scale);
  return tex;
}

/**
 * Generic sign for an OWNED commercial plot whose business has no placement yet (Townsy mark). Unowned
 * (AVAILABLE) shops show createAvailableCommercialTexture instead.
 */
export function createDefaultCommercialTexture(scene: Scene, atlas: SignAtlas): DynamicTexture {
  return createBusinessTexture(scene, "signDefault", { businessName: "Opening soon · Townsy", monogram: "T", brandColor: TOKENS.cobalt, accentColor: TOKENS.panel }, atlas);
}

/** Wording of the sign on an unowned (AVAILABLE) shop. */
export const AVAILABLE_SIGN_LINES = { title: "AVAILABLE", sub: "Buy this shop" } as const;

/**
 * Sign for an unowned shop (round 4: the world v3 shops start unowned): every atlas slot gets a
 * cobalt "AVAILABLE" band over a cream "Buy this shop" line with an amber rule, so a buyer can read
 * from the street that the building is for sale (the purchase itself happens in the property panel).
 * Deliberately unlike a business sign (no monogram tile, light panel).
 */
export function paintAvailableTexture(tex: DynamicTexture, atlas: SignAtlas): void {
  const ctx = ctx2d(tex);
  ctx.clearRect(0, 0, SIGN_TEXTURE.width, SIGN_TEXTURE.height);
  ctx.fillStyle = TOKENS.panel;
  ctx.fillRect(0, 0, SIGN_TEXTURE.width, SIGN_TEXTURE.height);
  const bold = makeMeasure(ctx, 800);
  const semi = makeMeasure(ctx, 700);
  for (const slot of atlas.slots) {
    const r = slot.region;
    const inline = slot.style === "inline";
    const pad = Math.max(4, Math.round(Math.min(r.w, r.h) * (inline ? 0.08 : 0.06)));
    const band: Region = { x: r.x + pad, y: r.y + pad, w: r.w - pad * 2, h: Math.round(r.h * (inline ? 0.6 : 0.56)) - pad };
    ctx.fillStyle = TOKENS.cobalt;
    roundedRect(ctx, band, Math.min(band.h * 0.16, 16));
    ctx.fill();
    const titlePx = fitFontSize(bold, AVAILABLE_SIGN_LINES.title, band.w * 0.88, band.h * 0.72, band.h * 0.72, 10);
    ctx.fillStyle = "#FFFFFF";
    ctx.font = font(titlePx, 800);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(AVAILABLE_SIGN_LINES.title, band.x + band.w / 2, band.y + band.h / 2 + titlePx * 0.04);
    const rule = Math.max(2, Math.round(pad * 0.4));
    const rest: Region = { x: r.x + pad, y: band.y + band.h + pad * 0.4, w: r.w - pad * 2, h: r.y + r.h - pad - rule * 2 - (band.y + band.h + pad * 0.4) };
    const subPx = fitFontSize(semi, AVAILABLE_SIGN_LINES.sub, rest.w * 0.9, rest.h * 0.86, rest.h * 0.86, 8);
    ctx.fillStyle = TOKENS.ink;
    ctx.font = font(subPx, 700);
    ctx.fillText(AVAILABLE_SIGN_LINES.sub, rest.x + rest.w / 2, rest.y + rest.h / 2 + subPx * 0.04);
    ctx.fillStyle = TOKENS.amber;
    ctx.fillRect(r.x + pad, r.y + r.h - pad * 0.5 - rule, r.w - pad * 2, rule);
    ctx.strokeStyle = "rgba(27,31,42,0.25)";
    ctx.lineWidth = Math.max(2, Math.round(pad * 0.25));
    ctx.strokeRect(r.x + ctx.lineWidth / 2, r.y + ctx.lineWidth / 2, r.w - ctx.lineWidth, r.h - ctx.lineWidth);
  }
  tex.update();
}

export function createAvailableCommercialTexture(scene: Scene, atlas: SignAtlas): DynamicTexture {
  const tex = makeDynamicTexture(scene, "signAvailable", SIGN_TEXTURE.width, SIGN_TEXTURE.height, { mips: true });
  tex.wrapU = Texture.CLAMP_ADDRESSMODE;
  tex.wrapV = Texture.CLAMP_ADDRESSMODE;
  paintAvailableTexture(tex, atlas);
  return tex;
}

export const CIVIC_SIGN_SPEC: BusinessSignSpec = {
  businessName: "Townsy Real Estate",
  monogram: "TR",
  brandColor: CIVIC_COLORS.sign,
  accentColor: CIVIC_COLORS.signAccent,
  houseGlyph: true,
};

/** Sign specs for every Townsy-run (civic) building kind; the name comes from the manifest. */
export function civicSignSpec(kind: string | undefined, name: string | undefined): BusinessSignSpec {
  switch (kind) {
    case "home_builders":
      return { businessName: name ?? "Townsy Home Builders", monogram: "HB", brandColor: "#B4521E", accentColor: "#F6E3C8", glyph: "builder" };
    case "furnishings":
      return { businessName: name ?? "Townsy Furnishings", monogram: "TF", brandColor: "#6A3D7A", accentColor: "#F3E6F5", glyph: "armchair" };
    // World v4 attractions: the building's own colours (town/attractions.ts).
    case "casino":
      return { businessName: name ?? "Townsy Casino", monogram: "TC", brandColor: "#A3132B", accentColor: "#E9B83E", glyph: "star" };
    case "race_track":
      return { businessName: name ?? "Townsy Downs", monogram: "TD", brandColor: "#1F6B41", accentColor: "#FFFFFF", glyph: "horseshoe" };
    case "sports_center":
      return { businessName: name ?? "Townsy Sports Center", monogram: "SC", brandColor: "#1F4FA3", accentColor: "#F28C28", glyph: "ball" };
    case "truck_arena":
      return { businessName: name ?? "Monster Truck Arena", monogram: "MT", brandColor: "#1B1B1F", accentColor: "#F5C518", glyph: "wheel" };
    // ADR-0044: the Sports Park's stadiums, in their home team's colours (town/stadiums.ts STADIUM_COLORS).
    case "football_stadium":
      return { businessName: name ?? "Titans Field", monogram: "TF", brandColor: "#1D2E5C", accentColor: "#F2B33D", glyph: "football" };
    case "soccer_stadium":
      return { businessName: name ?? "Townsy FC Park", monogram: "FC", brandColor: "#1E7A46", accentColor: "#FFFFFF", glyph: "soccer" };
    case "baseball_stadium":
      return { businessName: name ?? "Sluggers Ballpark", monogram: "SB", brandColor: "#B3202A", accentColor: "#F4EBD9", glyph: "baseball" };
    case "basketball_arena":
      return { businessName: name ?? "Hoopers Arena", monogram: "HA", brandColor: "#3B2A6B", accentColor: "#E8641B", glyph: "basketball" };
    case "hockey_arena":
      return { businessName: name ?? "Blizzard Ice Center", monogram: "BI", brandColor: "#1F5FA8", accentColor: "#EAF4FB", glyph: "puck" };
    default:
      return { ...CIVIC_SIGN_SPEC, businessName: name ?? CIVIC_SIGN_SPEC.businessName };
  }
}

/** One billboard face texture (all boards are 10 x 4 m: BILLBOARD_TEXTURE matches their aspect exactly). */
export function createBillboardFaceTexture(scene: Scene, name: string): DynamicTexture {
  const tex = makeDynamicTexture(scene, name, BILLBOARD_TEXTURE.width, BILLBOARD_TEXTURE.height, { mips: true });
  tex.wrapU = Texture.CLAMP_ADDRESSMODE;
  tex.wrapV = Texture.CLAMP_ADDRESSMODE;
  return tex;
}

/** Abstract skyline silhouettes (original art) along the bottom of a region, in `color`. */
function drawSkylineArt(ctx: Ctx2D, r: Region, color: string): void {
  const heights = [0.42, 0.7, 0.52, 0.92, 0.6, 1.0, 0.74, 0.46, 0.84, 0.56, 0.95, 0.5, 0.78, 0.62];
  const step = r.w / heights.length;
  ctx.fillStyle = color;
  heights.forEach((h, i) => {
    const w = step * 0.8;
    const x = r.x + i * step + (step - w) / 2;
    const top = r.y + r.h * (1 - h);
    ctx.fillRect(x, top, w, r.y + r.h - top);
  });
}

/**
 * The placeholder of an unrented board (M5): cobalt board, "Advertise here", an amber price pill
 * ("from $14/week" from the server's day rate) and the rental terms. Static: no flashing (docs/01).
 */
export function paintBillboardPlaceholder(tex: DynamicTexture, copy: PlaceholderCopy): void {
  const { width: W, height: H } = BILLBOARD_TEXTURE;
  const ctx = ctx2d(tex);
  const bold = makeScaledMeasure(ctx, 800);
  const semi = makeScaledMeasure(ctx, 600);
  const l = layoutPlaceholder(copy, bold, semi, W, H);
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#3361E8");
  g.addColorStop(1, TOKENS.cobalt);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  drawSkylineArt(ctx, l.art, "rgba(255,255,255,0.10)");
  ctx.strokeStyle = "rgba(251,248,242,0.55)";
  ctx.lineWidth = Math.max(4, Math.round(H * 0.014));
  ctx.strokeRect(l.frame.x, l.frame.y, l.frame.w, l.frame.h);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#FFFFFF";
  ctx.font = font(l.headline.fontPx, 800);
  for (const line of placeLines(l.headline, l.headlineBox)) ctx.fillText(line.text, line.x, line.y + l.headline.fontPx * 0.03);
  if (l.pill) {
    ctx.fillStyle = TOKENS.amber;
    roundedRect(ctx, l.pill.box, l.pill.box.h / 2);
    ctx.fill();
    ctx.fillStyle = TOKENS.ink;
    ctx.font = font(l.pill.fontPx, 800);
    ctx.fillText(l.pill.text, l.pill.box.x + l.pill.box.w / 2, l.pill.box.y + l.pill.box.h / 2 + l.pill.fontPx * 0.04);
  }
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.font = font(l.detailPx, 600);
  ctx.fillText(copy.detail, l.detailBox.x + l.detailBox.w / 2, l.detailBox.y + l.detailBox.h / 2);
  tex.update();
}

/** What a rented board shows from its approved creative (also the image creative's loading / error fallback). */
export interface BillboardCreativeSpec {
  title: string;
  monogram: string;
  brandColor: string;
  accentColor: string;
}

/** Text creative: brand board, white monogram tile, the title (<= 2 lines), an accent band. */
export function paintBillboardTextCreative(tex: DynamicTexture, c: BillboardCreativeSpec): void {
  const { width: W, height: H } = BILLBOARD_TEXTURE;
  const ctx = ctx2d(tex);
  const bold = makeScaledMeasure(ctx, 800);
  const l = layoutTextCreative(c.title, c.monogram, bold, W, H);
  ctx.fillStyle = c.brandColor;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = c.accentColor;
  ctx.fillRect(l.band.x, l.band.y, l.band.w, l.band.h);
  const inset = Math.round(l.pad * 0.3);
  ctx.strokeStyle = hexWithAlpha(c.accentColor, 0.55);
  ctx.lineWidth = Math.max(3, Math.round(H * 0.01));
  ctx.strokeRect(inset, inset, W - inset * 2, l.band.y - inset * 2);
  ctx.fillStyle = "#FFFFFF";
  roundedRect(ctx, l.tile, Math.min(l.tile.w, l.tile.h) * 0.16);
  ctx.fill();
  ctx.fillStyle = c.brandColor;
  ctx.font = font(l.monogramPx, 800);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(c.monogram, l.tile.x + l.tile.w / 2, l.tile.y + l.tile.h / 2 + l.monogramPx * 0.04);
  ctx.fillStyle = readableTextOn(c.brandColor);
  ctx.font = font(l.title.fontPx, 800);
  ctx.textAlign = "left";
  for (const line of placeLines(l.title, l.titleBox)) ctx.fillText(line.text, l.titleBox.x, line.y);
  tex.update();
}

/**
 * Image creative: the approved derivative contain-fit on the whole board (never stretched or
 * recoloured); any letterbox takes the image's own edge colour, else the brand colour.
 */
export function paintBillboardImage(tex: DynamicTexture, image: SignImage, fallbackColor: string): void {
  const { width: W, height: H } = BILLBOARD_TEXTURE;
  const ctx = ctx2d(tex);
  ctx.fillStyle = image.edgeColor ?? fallbackColor;
  ctx.fillRect(0, 0, W, H);
  const r = containRect(image.width, image.height, { x: 0, y: 0, w: W, h: H });
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image.source, r.x, r.y, r.w, r.h);
  tex.update();
}

/** The For Sale sign every unowned residential lot shows (one shared texture). */
export const FOR_SALE_LINES = { title: "FOR SALE", office: "Townsy Real Estate", address: "1 Plaza Way" } as const;

export function createForSaleTexture(scene: Scene, width = 512, height = 340): DynamicTexture {
  const tex = makeDynamicTexture(scene, "forSale", width, height, { mips: true });
  tex.wrapU = Texture.CLAMP_ADDRESSMODE;
  tex.wrapV = Texture.CLAMP_ADDRESSMODE;
  const ctx = ctx2d(tex);
  ctx.fillStyle = "#FBF8F2";
  ctx.fillRect(0, 0, width, height);
  const pad = Math.round(height * 0.06);
  const band: Region = { x: pad, y: pad, w: width - pad * 2, h: Math.round(height * 0.38) };
  ctx.fillStyle = "#C8302B";
  roundedRect(ctx, band, 14);
  ctx.fill();
  const bold = makeMeasure(ctx, 800);
  const titlePx = fitFontSize(bold, FOR_SALE_LINES.title, band.w - pad * 2, band.h * 0.72, band.h * 0.72, 18);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = font(titlePx, 800);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(FOR_SALE_LINES.title, width / 2, band.y + band.h / 2 + titlePx * 0.03);
  // House glyph left of the office name.
  const rest: Region = { x: pad, y: band.y + band.h + pad * 0.6, w: width - pad * 2, h: height - pad * 1.6 - band.h - band.y };
  const tile: Region = { x: rest.x, y: rest.y + rest.h * 0.08, w: rest.h * 0.84, h: rest.h * 0.84 };
  ctx.fillStyle = CIVIC_COLORS.sign;
  roundedRect(ctx, tile, tile.w * 0.16);
  ctx.fill();
  drawHouse(ctx, tile, "#FFFFFF");
  const textBox: Region = { x: tile.x + tile.w + pad * 0.7, y: rest.y, w: rest.x + rest.w - (tile.x + tile.w + pad * 0.7), h: rest.h };
  const m700 = makeMeasure(ctx, 700);
  const m500 = makeMeasure(ctx, 500);
  const officePx = fitFontSize(m700, FOR_SALE_LINES.office, textBox.w, textBox.h * 0.42, textBox.h * 0.42, 14);
  const addrPx = fitFontSize(m500, FOR_SALE_LINES.address, textBox.w, textBox.h * 0.34, Math.min(textBox.h * 0.34, officePx * 0.85), 12);
  ctx.textAlign = "left";
  ctx.fillStyle = TOKENS.ink;
  ctx.font = font(officePx, 700);
  ctx.fillText(FOR_SALE_LINES.office, textBox.x, textBox.y + textBox.h * 0.32);
  ctx.fillStyle = "#4A5060";
  ctx.font = font(addrPx, 500);
  ctx.fillText(FOR_SALE_LINES.address, textBox.x, textBox.y + textBox.h * 0.74);
  ctx.strokeStyle = "rgba(27,31,42,0.25)";
  ctx.lineWidth = 4;
  ctx.strokeRect(2, 2, width - 4, height - 4);
  tex.update();
  return tex;
}

export type BoardStyle = "tv" | "whiteboard" | "listings";

/** Store marketing board: dark TV screen or whiteboard with marker text; "No message yet" when empty. */
export function paintMessageBoard(tex: DynamicTexture, board: BoardText | null, style: "tv" | "whiteboard"): void {
  const { width: W, height: H } = tex.getSize();
  paintMessageBoardCanvas(ctx2d(tex), W, H, board, style);
  tex.update();
}

/** Real Estate office listings board: heading, "address ... price" rows, "+N more", footer. */
export function paintListingsBoard(tex: DynamicTexture, listings: readonly ListingLineText[] | null, copy: ListingsCopy = OFFICE_LISTINGS_COPY, accent: string = CIVIC_COLORS.sign): void {
  const { width: W, height: H } = tex.getSize();
  const ctx = ctx2d(tex);
  const bold = makeMeasure(ctx, 700);
  const regular = makeMeasure(ctx, 600);
  const l = layoutListingsBoard(listings, bold, regular, W, H, copy);
  ctx.fillStyle = "#F7F1E3";
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = accent;
  ctx.fillRect(0, 0, W, l.heading.box.y + l.heading.box.h + 6);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = font(l.heading.fontPx, 800);
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(l.heading.text, l.heading.box.x, l.heading.box.y + l.heading.box.h / 2 + 3);
  const rowH = l.rows.length > 1 ? l.rows[1]!.y - l.rows[0]!.y : l.rowBox.h / 6;
  l.rows.forEach((r, i) => {
    if (i % 2 === 1) {
      ctx.fillStyle = hexWithAlpha(accent, 0.08);
      ctx.fillRect(l.rowBox.x - 8, r.y - rowH / 2, l.rowBox.w + 16, rowH);
    }
    ctx.fillStyle = TOKENS.ink;
    ctx.font = font(l.rowFontPx, 600);
    ctx.textAlign = "left";
    ctx.fillText(r.address, l.rowBox.x, r.y);
    ctx.fillStyle = accent;
    ctx.font = font(l.rowFontPx, 700);
    ctx.textAlign = "right";
    ctx.fillText(r.price, l.rowBox.x + l.rowBox.w, r.y);
  });
  if (l.more) {
    ctx.fillStyle = "#4A5060";
    ctx.font = font(l.rowFontPx, 600);
    ctx.textAlign = "left";
    ctx.fillText(l.more.text, l.rowBox.x, l.more.y);
  }
  if (l.empty) {
    ctx.fillStyle = "#4A5060";
    ctx.font = font(Math.max(18, l.rowFontPx), 600);
    ctx.textAlign = "center";
    ctx.fillText(l.empty, W / 2, l.rowBox.y + l.rowBox.h / 2);
  }
  ctx.strokeStyle = accent;
  ctx.globalAlpha = 0.35;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(l.footer.box.x, l.footer.box.y - 4);
  ctx.lineTo(l.footer.box.x + l.footer.box.w, l.footer.box.y - 4);
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.fillStyle = accent;
  ctx.font = font(l.footer.fontPx, 600);
  ctx.textAlign = "center";
  ctx.fillText(l.footer.text, W / 2, l.footer.box.y + l.footer.box.h / 2);
  tex.update();
}

export function createBoardTexture(scene: Scene, name: string, aspect: number): DynamicTexture {
  const W = 1024;
  const H = Math.max(256, Math.round(W / aspect));
  const tex = makeDynamicTexture(scene, name, W, H, { mips: true });
  tex.wrapU = Texture.CLAMP_ADDRESSMODE;
  tex.wrapV = Texture.CLAMP_ADDRESSMODE;
  return tex;
}

export const NAME_TAG_TEXTURE = { width: 512, height: 128 } as const;

/** Flag image height on the 128 px tag texture (the 40 px source is drawn at about this size). */
const TAG_FLAG_H = 40;
const TAG_FLAG_MAX_W = 72;
const TAG_FLAG_GAP = 14;

/**
 * Name tag: label + country flag on a translucent ink pill. `flag` is an image (founder-provided flag set) or,
 * while it loads / when missing, the emoji text (letters on Windows, a globe for an unknown country).
 */
export function paintNameTag(tex: DynamicTexture, label: string, flag: TagFlag | string): Region {
  const f: TagFlag = typeof flag === "string" ? { kind: "text", text: flag } : flag;
  if (f.kind === "image") return paintNameTagWithImage(tex, label, f.image, f.aspect);
  const { width: W, height: H } = NAME_TAG_TEXTURE;
  const ctx = ctx2d(tex);
  ctx.clearRect(0, 0, W, H);
  const text = `${label} ${f.text}`;
  const measure = makeMeasure(ctx, 700, true);
  const px = fitFontSize(measure, text, W - 80, H * 0.6, 58, 18);
  const textW = measure(text, px);
  const pillW = Math.min(W - 8, textW + 56);
  const pill: Region = { x: (W - pillW) / 2, y: 14, w: pillW, h: H - 28 };
  ctx.fillStyle = "rgba(27,31,42,0.76)";
  roundedRect(ctx, pill, pill.h / 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.35)";
  ctx.lineWidth = 3;
  roundedRect(ctx, pill, pill.h / 2);
  ctx.stroke();
  ctx.fillStyle = "#FFFFFF";
  ctx.font = font(px, 700, true);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, W / 2, H / 2 + 2);
  tex.update();
  return pill;
}

function paintNameTagWithImage(tex: DynamicTexture, label: string, image: CanvasImageSource, aspect: number): Region {
  const { width: W, height: H } = NAME_TAG_TEXTURE;
  const ctx = ctx2d(tex);
  ctx.clearRect(0, 0, W, H);
  // Fit inside a TAG_FLAG_MAX_W x TAG_FLAG_H box keeping the flag's own shape (Nepal is taller than wide; most are ~1.5-2:1).
  const a = Number.isFinite(aspect) && aspect > 0 ? aspect : 1.5;
  const flagW = Math.round(Math.min(TAG_FLAG_MAX_W, TAG_FLAG_H * a));
  const flagH = Math.round(flagW / a);
  const measure = makeMeasure(ctx, 700, true);
  const px = fitFontSize(measure, label, W - 80 - flagW - TAG_FLAG_GAP, H * 0.6, 58, 18);
  const textW = measure(label, px);
  const contentW = textW + TAG_FLAG_GAP + flagW;
  const pillW = Math.min(W - 8, contentW + 56);
  const pill: Region = { x: (W - pillW) / 2, y: 14, w: pillW, h: H - 28 };
  ctx.fillStyle = "rgba(27,31,42,0.76)";
  roundedRect(ctx, pill, pill.h / 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.35)";
  ctx.lineWidth = 3;
  roundedRect(ctx, pill, pill.h / 2);
  ctx.stroke();
  const x0 = (W - contentW) / 2;
  ctx.fillStyle = "#FFFFFF";
  ctx.font = font(px, 700, true);
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(label, x0, H / 2 + 2);
  const fx = Math.round(x0 + textW + TAG_FLAG_GAP);
  const fy = Math.round(H / 2 - flagH / 2 + 1);
  // 160 px sources drawn smaller: high-quality downscaling keeps stripes and stars crisp.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, fx, fy, flagW, flagH);
  // Thin light outline so white/pale flags (e.g. Japan) stay visible on the dark pill.
  ctx.strokeStyle = "rgba(255,255,255,0.55)";
  ctx.lineWidth = 1.5;
  ctx.strokeRect(fx + 0.75, fy + 0.75, flagW - 1.5, flagH - 1.5);
  tex.update();
  return pill;
}

/**
 * An AI resident's name tag (ADR-0010): the same dark pill as every tag, the name, and a small
 * rounded cobalt "AI" badge where a visitor's flag would be (layout: nameTagLayout.ts).
 */
export function paintAiNameTag(tex: DynamicTexture, label: string): Region {
  const { width: W, height: H } = NAME_TAG_TEXTURE;
  const ctx = ctx2d(tex);
  ctx.clearRect(0, 0, W, H);
  const l = layoutAiNameTag(makeMeasure(ctx, 700, true), label, W, H);
  ctx.fillStyle = "rgba(27,31,42,0.76)";
  roundedRect(ctx, l.pill, l.pill.h / 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.35)";
  ctx.lineWidth = 3;
  roundedRect(ctx, l.pill, l.pill.h / 2);
  ctx.stroke();
  ctx.fillStyle = "#FFFFFF";
  ctx.font = font(l.label.fontPx, 700, true);
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(label, l.label.x, l.label.y);
  ctx.fillStyle = AI_BADGE.fill;
  roundedRect(ctx, l.badge, l.badge.h / 2);
  ctx.fill();
  ctx.strokeStyle = AI_BADGE.outline;
  ctx.lineWidth = 2.5;
  roundedRect(ctx, l.badge, l.badge.h / 2);
  ctx.stroke();
  ctx.fillStyle = AI_BADGE.ink;
  ctx.font = font(l.badge.fontPx, 800);
  ctx.textAlign = "center";
  ctx.fillText(AI_BADGE.text, l.badge.textX, l.badge.textY);
  tex.update();
  return l.pill;
}

/** A name tag texture and its painted pill (texture px; the speech layout keeps bubbles off other tags' pills). */
export function createNameTagTexture(scene: Scene, name: string, label: string, flag: TagFlag | string, ai = false): { texture: DynamicTexture; pill: Region } {
  const texture = makeDynamicTexture(scene, name, NAME_TAG_TEXTURE.width, NAME_TAG_TEXTURE.height, { alpha: true, mips: true });
  const pill = ai ? paintAiNameTag(texture, label) : paintNameTag(texture, label, flag);
  return { texture, pill };
}

/** Obvious exit mat for interiors: amber mat, EXIT label and an arrow pointing at the door. */
export function createExitMatTexture(scene: Scene): DynamicTexture {
  const W = 512;
  const H = 320;
  const tex = makeDynamicTexture(scene, "exitMat", W, H, { mips: true });
  const ctx = ctx2d(tex);
  ctx.fillStyle = TOKENS.amber;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = TOKENS.ink;
  ctx.lineWidth = 10;
  ctx.strokeRect(16, 16, W - 32, H - 32);
  ctx.fillStyle = TOKENS.ink;
  ctx.font = font(120, 800);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("EXIT", W / 2, 118);
  // Arrow pointing "down" on the texture = toward the door when the mat is laid in front of it.
  ctx.beginPath();
  ctx.moveTo(W / 2 - 70, 200);
  ctx.lineTo(W / 2 + 70, 200);
  ctx.lineTo(W / 2 + 70, 240);
  ctx.lineTo(W / 2 + 120, 240);
  ctx.lineTo(W / 2, 300);
  ctx.lineTo(W / 2 - 120, 240);
  ctx.lineTo(W / 2 - 70, 240);
  ctx.closePath();
  ctx.fill();
  tex.update();
  return tex;
}

/** Small lit "EXIT" sign on the door post (replaces the old full-width lintel). */
export function createExitSignTexture(scene: Scene): DynamicTexture {
  const W = 256;
  const H = 96;
  const tex = makeDynamicTexture(scene, "exitSign", W, H, { mips: true });
  const ctx = ctx2d(tex);
  ctx.fillStyle = TOKENS.cobalt;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = font(64, 800);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("EXIT", W / 2, H / 2 + 3);
  tex.update();
  return tex;
}

/** Simple centred label texture. */
export function createLabelTexture(scene: Scene, name: string, label: string, bg: string, fg: string, width = 512, height = 154): DynamicTexture {
  const tex = makeDynamicTexture(scene, name, width, height, { mips: true });
  const ctx = ctx2d(tex);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, width, height);
  const measure = makeMeasure(ctx);
  const box: Region = { x: 24, y: 16, w: width - 48, h: height - 32 };
  const block = fitTextBlock(measure, label, box, { maxPx: height * 0.5, minPx: 14, maxLines: 2 });
  ctx.fillStyle = fg;
  ctx.font = font(block.fontPx, 700);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (const line of placeLines(block, box)) ctx.fillText(line.text, line.x, line.y);
  tex.update();
  return tex;
}
